import { useState, useEffect, useCallback, useMemo } from "react";

/**
 * Hook personalizado para peticiones GET que se ejecutan automáticamente
 * cuando cambian los parámetros y cumplen con la condición especificada
 *
 * @param {Function} apiFunction - Función de API a ejecutar
 * @param {*} params - Parámetros para la función de API. Si es falsy, no se ejecuta automáticamente
 * @param {boolean} shouldExecute - Condición booleana para determinar si ejecutar (por defecto true)
 * @returns {Object} Objeto con data, error, isLoading, execute y refetch
 *
 * @example
 * // Ejecutar cuando usuario esté autenticado y tenga ID válido
 * const { data, isLoading } = useQuery(
 *   getUsuarioEstadisticas,
 *   { idUsuario: 123 },
 *   isAuthenticated
 * );
 */
export const useQuery = (apiFunction, params = null, shouldExecute = true) => {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const memoizedParams = useMemo(() => params, [JSON.stringify(params)]);

  const execute = useCallback(
    async (newParams = null) => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await apiFunction(newParams || memoizedParams);
        setData(result);
        return result;
      } catch (err) {
        setError(err);
        console.error("Error useQuery:", err.message || err);
      } finally {
        setIsLoading(false);
      }
    },
    [apiFunction, memoizedParams]
  );

  useEffect(() => {
    const canExecute = shouldExecute && Boolean(memoizedParams);

    if (canExecute) {
      execute();
    }
  }, [execute, memoizedParams, shouldExecute]);

  const refetch = useCallback(async () => await execute(), [execute]);

  return { data, error, isLoading, execute, refetch };
};
